import {useTranslation} from 'react-i18next';
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {PlaylistItem} from '../../../../../../../backend/interfaces/playlist-item';
import {PaginatedTableScreen} from '../../../../../../../components/paginated-table-screen/paginated-table-screen';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';
import {useState} from 'react';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import AddIcon from '@mui/icons-material/Add';

// Columns IDs
enum ColumnIdentifier {
  Title = 'title',
}

export default function PlaylistItemsSystemAdministratorOfficeScreen() {
  // Get params
  let {id}: {id?: string} = useParams();

  // Hold state
  const [deletedPlaylistItemName, setDeletedPlaylistItemName] = useState<string | null>(
    new URLSearchParams(useLocation().search).get(ScreenPaths.PlaylistItemDeletedQuery)
  );

  // Translations
  const {t} = useTranslation();

  // Get navigation info
  let history = useHistory();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Fetch playlist items
  const handleGetPlaylistItems = async (limit: number, skip: number, search?: string) => {
    // Check id
    if (!id) {
      throw new Error('Empty id.');
    }

    // Load items
    const result: {totalCount: number; data: Array<PlaylistItem>} =
      await PlaylistsRepository.instance.getPlaylistItemsAsSystemAdministrator(
        id,
        skip,
        limit,
        true,
        search
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistItem> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        avatarUrl: obj.thumbnailUrl,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Title,
            value: obj.title,
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedPlaylistItemName(null);

    // Reset history
    history.replace(url);
  };
  // change PaginatedTableScreen to PaginatedTable
  return (
    <>
      <PaginatedTableScreen
        pageHeader={{
          searchLabel: t(
            translations.playlists.system_administrator.edit.items.search_input_placeholder
          ),
          rightButton: {
            label: t(translations.playlists.system_administrator.edit.items.add_button),
            toLink: url.charAt(url.length - 1) === '/' ? `${url}create` : `${url}/create`,
            endIcon: <AddIcon />,
          },
        }}
        emptyTableMessage={t(translations.playlists.system_administrator.edit.items.no_items)}
        tableColumns={[
          {
            columnId: ColumnIdentifier.Title,
            title: t(translations.playlists.system_administrator.edit.items.title_header),
            flexGrow: 1,
          },
        ]}
        fetchData={handleGetPlaylistItems}
        linkableRows={true}
        avatar={{
          isIcon: false,
          type: 'other',
        }}
      />
      <AlertModal
        id="playlist-item-deleted-warning"
        opened={deletedPlaylistItemName ? true : false}
        title={t(
          translations.playlists.system_administrator.edit.items.deleted_playlist_item_dialog_title,
          {
            name: deletedPlaylistItemName,
          }
        )}
        content={t(
          translations.playlists.system_administrator.edit.items
            .deleted_playlist_item_dialog_message
        )}
        handleClose={handleCloseDialog}
        actions={[
          {
            label: t(
              translations.playlists.system_administrator.edit.items
                .deleted_playlist_item_dialog_ok_button
            ),
            type: 'neutral',
            handleClick: handleCloseDialog,
          },
        ]}
      />
    </>
  );
}
